import React, { Dispatch, SetStateAction } from 'react';

import { authAxios, getEmailFromSessionStorage } from '../../utils/session_utils';
import { message, Modal, Typography } from 'antd';
import { CopyOutlined } from '@ant-design/icons';

type StructureMismatch =
	| { kind: 'missing_day_in_sheet'; day: string; templateDurations: number[] }
	| { kind: 'extra_day_in_sheet'; day: string; sheetDurations: number[] }
	| {
		kind: 'count_mismatch';
		day: string;
		templateCount: number;
		sheetCount: number;
		templateDurations: number[];
		sheetDurations: number[];
	  }
	| {
		kind: 'duration_mismatch';
		day: string;
		position: number;
		expected: number;
		actual: number;
	  };

interface TemplateSheetMismatchDetails {
	mismatches: StructureMismatch[];
	templateBreaksByDay: { [day: string]: number[] };
	sheetBreaksByDay: { [day: string]: number[] };
}

const fmtDurations = (ds: number[]) => `[${ds.map((d) => `${d}s`).join(', ')}]`;

function describeMismatch(m: StructureMismatch): React.ReactNode {
	switch (m.kind) {
		case 'missing_day_in_sheet':
			return (
				<span>
					<b>{m.day}</b> — sheet is missing this day&apos;s breaks. Template expects{' '}
					{m.templateDurations.length} break(s): {fmtDurations(m.templateDurations)}
				</span>
			);
		case 'extra_day_in_sheet':
			return (
				<span>
					<b>{m.day}</b> — sheet has {m.sheetDurations.length} break(s) for this day{' '}
					{fmtDurations(m.sheetDurations)} but the template doesn&apos;t
				</span>
			);
		case 'count_mismatch':
			return (
				<span>
					<b>{m.day}</b> — template has {m.templateCount} break(s){' '}
					{fmtDurations(m.templateDurations)}, sheet has {m.sheetCount}{' '}
					{fmtDurations(m.sheetDurations)}
				</span>
			);
		case 'duration_mismatch':
			return (
				<span>
					<b>{m.day}</b> position {m.position + 1} — template duration is{' '}
					<b>{m.expected}s</b>, sheet has <b>{m.actual}s</b>
				</span>
			);
	}
}

function TemplateSheetMismatchContent({ details }: { details: TemplateSheetMismatchDetails }) {
	return (
		<div>
			<p>
				The Post sheet&apos;s break structure doesn&apos;t match the program template attached
				to this batch job. Fix the sheet (or the wrong sheet was selected) and try
				again.
			</p>
			<p style={{ marginBottom: 4 }}>
				<b>Mismatch{details.mismatches.length === 1 ? '' : 'es'}:</b>
			</p>
			<ul style={{ marginTop: 0, paddingLeft: 20 }}>
				{details.mismatches.map((m, i) => (
					<li key={i} style={{ marginBottom: 4 }}>
						{describeMismatch(m)}
					</li>
				))}
			</ul>
			<details style={{ marginTop: 8 }}>
				<summary style={{ cursor: 'pointer' }}>Show full structures</summary>
				<div style={{ display: 'flex', gap: 24, marginTop: 8 }}>
					<div>
						<b>Template:</b>
						<pre style={{ fontSize: 12, margin: 0 }}>
							{Object.entries(details.templateBreaksByDay)
								.map(([d, ds]) => `${d}: ${fmtDurations(ds)}`)
								.join('\n')}
						</pre>
					</div>
					<div>
						<b>Sheet:</b>
						<pre style={{ fontSize: 12, margin: 0 }}>
							{Object.entries(details.sheetBreaksByDay)
								.map(([d, ds]) => `${d}: ${fmtDurations(ds)}`)
								.join('\n')}
						</pre>
					</div>
				</div>
			</details>
		</div>
	);
}

export const useBatchJobsActionHook = (setIsLoading: Dispatch<SetStateAction<boolean>>, onRefresh?: () => Promise<void>) => {
	const handleUploadSFClick = async (data: any) => {
		let userNameFromEmail = getEmailFromSessionStorage();
		userNameFromEmail = userNameFromEmail ? userNameFromEmail.split('@')[0] : 'unknown_user';


		const config: any = {
			method: "POST",
			url: `${process.env.REACT_APP_BACK_END_API}/snowflake/batch-jobs/upload-autopodder-prelogs`,
			headers: {
				"Content-Type": "application/json",
			},
			data: JSON.stringify({
				programName: data.programName,
				strategyName: data.strategyName,
				batchJobId: data.batchJobId,
				programVersion: data.programVersion,
				createdBy: userNameFromEmail,
			}),
		};
		try {
			const response = await authAxios(config);
			if (response.status === 200) {
				message.success(response.data.message + ' ' + response.data.result.Location);
			} else {
				message.error(response.data.message);
			}
		} catch (err: any) {
			// Non-2xx throws here (e.g. 404 when a strategy's SF csv isn't in
			// job_result_json yet). Surface the backend's error and clear the spinner.
			message.error(err.response?.data?.error || err.response?.data?.message || err.message || "SF upload failed");
		} finally {
			setIsLoading(false);
		}
	}

	const handleGenerateSyndicationSheet = async (batchJobId: string, strategyName: string) => {
		setIsLoading(true);
		try {
			const config: any = {
				method: "POST",
				url: `${process.env.REACT_APP_BACK_END_API}/snowflake/batch-jobs/autopodder/generate-syndication-sheet`,
				headers: {
					"Content-Type": "application/json",
				},
				data: JSON.stringify({ batchJobId, strategyName }),
			};
			const response = await authAxios(config);
			if (response.status === 200) {
				message.success(response.data.message);
				// Refresh the batch jobs list to show the new spreadsheet link
				if (onRefresh) {
					await onRefresh();
				}
			} else {
				message.error(response.data.err || "Failed to generate syndication sheet");
			}
		} catch (err: any) {
			message.error(err.response?.data?.err || "Failed to generate syndication sheet");
		} finally {
			setIsLoading(false);
		}
	}

	const handleUpdateExistingSheet = async (batchJobId: string, strategyName: string, spreadsheetId: string) => {
		setIsLoading(true);
		try {
			const config: any = {
				method: "POST",
				url: `${process.env.REACT_APP_BACK_END_API}/snowflake/batch-jobs/autopodder/update-existing-sheet`,
				headers: {
					"Content-Type": "application/json",
				},
				data: JSON.stringify({ batchJobId, strategyName, spreadsheetId }),
			};
			const response = await authAxios(config);
			if (response.status === 200) {
				message.success(response.data.message);
				if (onRefresh) {
					await onRefresh();
				}
			} else {
				message.error(response.data.err || "Failed to update existing sheet");
			}
		} catch (err: any) {
			const data = err.response?.data;
			const errMsg: string = data?.err || "Failed to update existing sheet";

			if (data?.errorCode === "TEMPLATE_SHEET_MISMATCH") {
				Modal.error({
					title: "Post Sheet Doesn't Match Program Template",
					width: 720,
					content: <TemplateSheetMismatchContent details={data.details} />,
				});
				return;
			}

			const serviceEmail = "autopodderserviceacc@autopodder.iam.gserviceaccount.com";
			const hasEmail = errMsg.includes(serviceEmail);
			Modal.error({
				title: "Update Existing Sheet Failed",
				width: 600,
				content: hasEmail ? (
					<div>
						<p>{errMsg.split(serviceEmail)[0]}</p>
						<Typography.Text code copyable>{serviceEmail}</Typography.Text>
						<p>{errMsg.split(serviceEmail)[1]}</p>
					</div>
				) : (
					<pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 13 }}>{errMsg}</pre>
				),
			});
		} finally {
			setIsLoading(false);
		}
	}

	const handleResubmitJob = async (batchJobId: string) => {
		setIsLoading(true);
		try {
			const config: any = {
				method: "POST",
				url: `${process.env.REACT_APP_BACK_END_API}/snowflake/batch-jobs/resubmit`,
				headers: {
					"Content-Type": "application/json",
				},
				data: JSON.stringify({ batchJobId }),
			};
			const response = await authAxios(config);
			if (response.status === 200) {
				message.success(response.data.message);
				if (onRefresh) {
					await onRefresh();
				}
			} else {
				message.error(response.data.err || "Failed to resubmit job");
			}
		} catch (err: any) {
			message.error(err.response?.data?.err || "Failed to resubmit job");
		} finally {
			setIsLoading(false);
		}
	}

	const handleDeleteBatchJob = async (batchJobId: string) => {
		setIsLoading(true);
		try {
			const config: any = {
				method: "DELETE",
				url: `${process.env.REACT_APP_BACK_END_API}/snowflake/batch-jobs/${batchJobId}`,
				headers: {
					"Content-Type": "application/json",
				},
			};
			const response = await authAxios(config);
			if (response.status === 200) {
				message.success(response.data.message);
				if (onRefresh) {
					await onRefresh();
				}
			} else {
				message.error(response.data.err || "Failed to delete batch job");
			}
		} catch (err: any) {
			message.error(err.response?.data?.err || "Failed to delete batch job");
		} finally {
			setIsLoading(false);
		}
	}

	return { handleUploadSFClick, handleGenerateSyndicationSheet, handleUpdateExistingSheet, handleResubmitJob, handleDeleteBatchJob, setIsLoading };
};

